import dayjs from "dayjs";

function resolveScheduleTimeMs(value: string | number): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d+$/.test(trimmed)) {
      const epochMs = Number(trimmed);
      return Number.isFinite(epochMs) && epochMs > 0 ? epochMs : null;
    }

    const parsed = dayjs(trimmed);
    if (parsed.isValid()) {
      return parsed.valueOf();
    }
  }

  return null;
}

/**
 * Format notification time:
 * - same day: relative short label like 3h / 25m
 * - earlier days: month/day + hour/minute
 */
export const formatNotificationGeneratedTime = (
  generatedAtMs: number,
  nowMs = Date.now(),
  locale?: string,
) => {
  const generatedAt = dayjs(generatedAtMs);
  const now = dayjs(nowMs);

  if (!generatedAt.isValid()) {
    return "";
  }

  if (generatedAt.isSame(now, "day")) {
    const diffMinutes = Math.max(0, now.diff(generatedAt, "minute"));
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });

    if (diffMinutes < 60) {
      return rtf.format(-Math.max(1, diffMinutes), "minute");
    }

    const diffHours = Math.max(1, now.diff(generatedAt, "hour"));
    return rtf.format(-diffHours, "hour");
  }

  return generatedAt.format("MM/DD HH:mm");
};

/** Tracker History sheet meta — always absolute calendar time (Figma 87676:37795). */
export const formatTrackerSignalTrackedOnTime = (
  trackedAtMs: number,
  locale?: string,
) => {
  const trackedAt = dayjs(trackedAtMs);
  if (!trackedAt.isValid()) {
    return "";
  }
  return trackedAt.locale(locale ?? "en").format("MM/DD HH:mm");
};

/** Tracker next schedule — epoch ms / numeric string / ISO-8601 datetime. */
export const formatTrackerNextScheduleTime = (
  value: string | number,
  locale?: string,
) => {
  const epochMs = resolveScheduleTimeMs(value);
  if (epochMs != null) {
    const scheduledAt = dayjs(epochMs);
    if (!scheduledAt.isValid()) {
      return "";
    }
    return scheduledAt.locale(locale ?? "en").format("YYYY/MM/DD HH:mm");
  }

  return typeof value === "string" ? value : "";
};

/** Invest Moves card footer — Figma 94917:42894 「7 Days ago」 */
export const formatInvestMovesRelativeTime = (
  generatedAtMs: number,
  nowMs = Date.now(),
) => {
  const generatedAt = dayjs(generatedAtMs);
  const now = dayjs(nowMs);

  if (!generatedAt.isValid()) {
    return "";
  }

  const diffMinutes = Math.max(0, now.diff(generatedAt, "minute"));
  if (diffMinutes < 60) {
    return diffMinutes <= 1 ? "1 Minute ago" : `${diffMinutes} Minutes ago`;
  }

  const diffHours = Math.max(1, now.diff(generatedAt, "hour"));
  if (diffHours < 24) {
    return diffHours === 1 ? "1 Hour ago" : `${diffHours} Hours ago`;
  }

  const diffDays = Math.max(1, now.diff(generatedAt, "day"));
  return diffDays === 1 ? "1 Day ago" : `${diffDays} Days ago`;
};
